import { ApolloClient, InMemoryCache, createHttpLink, from } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getMsalInstance, loginRequest } from "./authConfig";
import { getCachedConfig } from "./services/fabricConfig";

// Apollo Client instance - will be created dynamically
let apolloClient = null;

/**
 * Get access token from MSAL
 * @returns {Promise<string|null>} Access token or null
 */
const getAccessToken = async () => {
  const msalInstance = getMsalInstance();
  
  if (!msalInstance) {
    console.warn("MSAL instance not initialized");
    return null;
  }

  const accounts = msalInstance.getAllAccounts();
  
  if (accounts.length === 0) {
    return null;
  }

  try {
    // Try to acquire token silently first
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (error) {
    console.error("Silent token acquisition failed:", error);
    
    // If silent acquisition fails, redirect to login
    // This avoids COOP issues with popups
    try {
      await msalInstance.acquireTokenRedirect(loginRequest);
      // Page will redirect, return null for now
      return null;
    } catch (redirectError) {
      console.error("Token acquisition via redirect failed:", redirectError);
      // Redirect to login page
      window.location.href = "/authentication/sign-in/basic";
      return null;
    }
  }
};

/**
 * Create Apollo Client with the given GraphQL endpoint
 * @param {string} graphQLEndpoint - The GraphQL API endpoint
 * @returns {ApolloClient} Configured Apollo Client instance
 */
const createApolloClient = (graphQLEndpoint) => {
  // Create HTTP link
  const httpLink = createHttpLink({
    uri: graphQLEndpoint,
  });

  // Auth link to add token to requests
  const authLink = setContext(async (_, { headers }) => {
    // Get token from MSAL
    const token = await getAccessToken();
    
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
      },
    };
  });

  // Error handling link
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        );
      });
    }
    
    if (networkError) {
      console.error(`[Network error]: ${networkError}`);
      
      // Handle 401 unauthorized - redirect to login
      if (networkError.statusCode === 401) {
        // Clear any stored tokens
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("userInfo");
        
        // Redirect to login page
        window.location.href = "/authentication/sign-in/basic";
      }
    }
  });

  // Create Apollo Client instance
  return new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network",
        errorPolicy: "all",
      },
      query: {
        fetchPolicy: "network-only",
        errorPolicy: "all",
      },
      mutate: {
        errorPolicy: "all",
      },
    },
  });
};

/**
 * Initialize Apollo Client with config from fabric API
 * @returns {ApolloClient} Initialized Apollo Client
 */
export const initializeApolloClient = () => {
  if (apolloClient) {
    return apolloClient;
  }

  // Get GraphQL endpoint from cached fabric config
  const fabricConfig = getCachedConfig();
  
  if (!fabricConfig || !fabricConfig.graphQLEndpoint) {
    console.error("Fabric config not available or missing graphQLEndpoint");
    // Fallback to environment variable or default
    const fallbackEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT || "https://app.doorsharp.com/graphql";
    apolloClient = createApolloClient(fallbackEndpoint);
    return apolloClient;
  }

  apolloClient = createApolloClient(fabricConfig.graphQLEndpoint);
  console.log("Apollo Client initialized with endpoint:", fabricConfig.graphQLEndpoint);
  
  return apolloClient;
};

/**
 * Get the current Apollo Client instance
 * @returns {ApolloClient|null}
 */
export const getApolloClient = () => apolloClient;

export default apolloClient;
